import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { graphqlRequest } from '../../utils/graphqlRequest';
import ActivityPlaceholderTable from './ActivityPlaceholderTable';
import { ADMIN_PORTAL_BASE } from '../../constants/adminPortal';
import '../../styles/AdminUserActivityHub.css';

const TABS = [
  { id: 'sessions', label: 'Active sessions' },
  { id: 'login-history', label: 'Login history' },
];

const PAGE_SIZE = 10;

const GET_ADMIN_ACTIVE_SESSIONS = `
  query GetAdminActiveSessions {
    getAdminActiveSessions {
      userId
      name
      roles
      ipAddress
      device
      lastActivityAt
      status
    }
  }
`;

const GET_ADMIN_LOGIN_HISTORY = `
  query GetAdminLoginHistory {
    getAdminLoginHistory {
      userId
      name
      roles
      ipAddress
      device
      loginMethod
      success
      failureReason
      occurredAt
    }
  }
`;

function normalizeTab(raw) {
  const t = String(raw || '').trim().toLowerCase();
  if (t === 'login-history' || t === 'history' || t === 'login') {
    return 'login-history';
  }
  return 'sessions';
}

function mapSessionRow(item) {
  return [
    item?.name || 'Unknown',
    Array.isArray(item?.roles) ? item.roles.join(', ') : '-',
    item?.ipAddress || '-',
    item?.device || '-',
    item?.lastActivityAt
      ? new Date(item.lastActivityAt).toLocaleString()
      : '-',
    item?.status || '-',
  ];
}

function mapHistoryRow(item) {
  return [
    item?.name || 'Unknown',
    Array.isArray(item?.roles) ? item.roles.join(', ') : '-',
    item?.ipAddress || '-',
    item?.device || '-',
    item?.occurredAt ? new Date(item.occurredAt).toLocaleString() : '-',
    item?.success ? 'Success' : item?.failureReason || 'Failed',
  ];
}

function PaginationBar({ page, totalPages, total, onPageChange }) {
  if (total <= PAGE_SIZE) return null;
  const current = Math.min(page, totalPages);
  const start = (current - 1) * PAGE_SIZE + 1;
  const end = Math.min(current * PAGE_SIZE, total);
  return (
    <div className="uah-pagination">
      <span>
        Showing {start}–{end} of {total}
      </span>
      <div className="uah-pagination-controls">
        <button
          type="button"
          disabled={current <= 1}
          onClick={() => onPageChange(Math.max(1, current - 1))}
          aria-label="Previous page"
        >
          ‹
        </button>
        <span className="uah-pagination-page">
          Page {current} of {totalPages}
        </span>
        <button
          type="button"
          disabled={current >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, current + 1))}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    </div>
  );
}

export default function UserActivityHub() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get('tab'));

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);

  const setTab = (nextId) => {
    setSearchParams({ tab: nextId }, { replace: true });
  };

  useEffect(() => {
    setSessionsPage(1);
    setHistoryPage(1);
  }, [tab]);

  useEffect(() => {
    if (tab !== 'sessions') return undefined;
    let cancelled = false;
    (async () => {
      setSessionsLoading(true);
      try {
        const data = await graphqlRequest(GET_ADMIN_ACTIVE_SESSIONS);
        if (!cancelled) {
          setSessions(
            Array.isArray(data?.getAdminActiveSessions)
              ? data.getAdminActiveSessions
              : [],
          );
          setSessionsPage(1);
        }
      } catch (error) {
        console.error('Failed to load active sessions:', error);
        if (!cancelled) setSessions([]);
      } finally {
        if (!cancelled) setSessionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab]);

  useEffect(() => {
    if (tab !== 'login-history') return undefined;
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      try {
        const data = await graphqlRequest(GET_ADMIN_LOGIN_HISTORY);
        if (!cancelled) {
          setHistory(
            Array.isArray(data?.getAdminLoginHistory)
              ? data.getAdminLoginHistory
              : [],
          );
          setHistoryPage(1);
        }
      } catch (error) {
        console.error('Failed to load login history:', error);
        if (!cancelled) setHistory([]);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const sessionsTotalPages = Math.max(
    1,
    Math.ceil(sessions.length / PAGE_SIZE),
  );
  const sessionsCurrentPage = Math.min(sessionsPage, sessionsTotalPages);

  const historyTotalPages = Math.max(1, Math.ceil(history.length / PAGE_SIZE));
  const historyCurrentPage = Math.min(historyPage, historyTotalPages);

  const sessionRows = useMemo(() => {
    if (sessionsLoading) return [['Loading...', '-', '-', '-', '-', '-']];
    if (!sessions.length) {
      return [['No active sessions found', '-', '-', '-', '-', '-']];
    }
    const start = (sessionsCurrentPage - 1) * PAGE_SIZE;
    return sessions.slice(start, start + PAGE_SIZE).map(mapSessionRow);
  }, [sessions, sessionsLoading, sessionsCurrentPage]);

  const historyRows = useMemo(() => {
    if (historyLoading) return [['Loading...', '-', '-', '-', '-', '-']];
    if (!history.length) {
      return [['No login history found', '-', '-', '-', '-', '-']];
    }
    const start = (historyCurrentPage - 1) * PAGE_SIZE;
    return history.slice(start, start + PAGE_SIZE).map(mapHistoryRow);
  }, [history, historyLoading, historyCurrentPage]);

  return (
    <div className="uah-page">
      <button
        type="button"
        className="uah-back"
        onClick={() => navigate(ADMIN_PORTAL_BASE)}
      >
        <FaArrowLeft aria-hidden />
        Back to dashboard
      </button>

      <header className="uah-header">
        <h1>User activity</h1>
        <p>
          Company-wide sessions and login history for admin oversight. Your own
          devices and login log live under My profile.
        </p>
      </header>

      <nav className="uah-tabs" role="tablist" aria-label="User activity sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={
              tab === t.id ? 'uah-tab uah-tab--active' : 'uah-tab'
            }
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="uah-panel" role="tabpanel">
        {tab === 'sessions' ? (
          <>
            <ActivityPlaceholderTable
              title="Active sessions"
              subtitle="Currently signed-in users and live device sessions across the company."
              columns={[
                'Name',
                'Role',
                'IP Address',
                'Device',
                'Last Activity',
                'Status',
              ]}
              rows={sessionRows}
            />
            {!sessionsLoading ? (
              <PaginationBar
                page={sessionsCurrentPage}
                totalPages={sessionsTotalPages}
                total={sessions.length}
                onPageChange={setSessionsPage}
              />
            ) : null}
          </>
        ) : (
          <>
            <ActivityPlaceholderTable
              title="Login history"
              subtitle="Recent authentication activity across admin and workforce users."
              columns={[
                'Name',
                'Role',
                'IP Address',
                'Device',
                'Login Time',
                'Status',
              ]}
              rows={historyRows}
            />
            {!historyLoading ? (
              <PaginationBar
                page={historyCurrentPage}
                totalPages={historyTotalPages}
                total={history.length}
                onPageChange={setHistoryPage}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
